/**
 * English: the source every other dictionary is checked against.
 *
 * Keys ending in `_one` and `_other` are a plural pair; use `plural()` for
 * those. `{name}` is filled in at the call site.
 */
export const en = {
  'common.loading': 'Loading…',
  'common.save': 'Save',
  'common.saving': 'Saving…',
  'common.saved': 'Saved.',
  'common.cancel': 'Cancel',
  'common.edit': 'Edit',
  'common.delete': 'Delete',
  'common.reallyDelete': 'Really delete?',
  'common.remove': 'Remove',
  'common.reallyRemove': 'Really remove?',
  'common.add': 'Add',
  'common.create': 'Create',
  'common.creating': 'Creating…',
  'common.none': 'None',
  'common.anyone': 'Anyone',
  'common.name': 'Name',
  'common.email': 'Email',
  'common.password': 'Password',
  'common.station': 'Station',
  'common.anyStation': 'Any station',
  'common.anywhere': 'Anywhere',
  'common.optional': 'Optional',
  'common.moveUp': 'Move up',
  'common.moveDown': 'Move down',
  'common.unit': 'Unit',
  'common.howMuch': 'How much',
  'common.amount': 'Amount',
  'common.working': 'Calculating…',
  'common.dismiss': 'Dismiss',
  'common.tryAgain': 'Try again',
  'common.loadFailed': 'Couldn’t load this. Check the connection and try again.',

  'error.generic': 'Something went wrong.',
  'error.notFound': 'That isn’t there any more. Someone may have removed it.',
  'error.validationFailed': 'Something in that isn’t filled in right. Check it and try again.',
  'error.alreadyExists': 'That already exists.',
  'error.stillReferenced': 'That is still used somewhere else, so it can’t be removed.',
  'error.invalidData': 'That value isn’t allowed here.',
  'error.writeInProgress': 'That change is being saved. Try again in a moment.',
  'error.backupFileMissing': 'Attach the backup file.',
  'error.onedriveNotConnected': 'Connect OneDrive first.',
  'error.nasAddressNeeded': 'Enter the address of the NAS to send backups to.',
  'error.fileTooLarge': 'That file is too large.',
  'error.unknownTimeZone': 'That time zone isn’t one this server knows.',
  'error.alreadySetUp': 'This server has already been set up.',
  'error.imageType': 'Use a JPEG, PNG or WebP image.',
  'error.imageMissing': 'No image was attached.',
  'error.notADish': 'Only a dish has a dish card.',
  'error.logoTooLarge': 'That logo is larger than 2 MB.',
  'error.photoTooLarge': 'That image is larger than 8 MB.',
  'error.taskNotYours': 'You can only put your own name against a line.',
  'error.taskChefOnly': 'You can start and finish a line, but only a chef can change what it says.',
  'error.staffRank': 'Only an administrator can manage staff at your rank or above.',
  'error.adminNeedsPassword':
    'An administrator needs an email address and password, not only a PIN.',
  'error.passwordNeedsEmail': 'Signing in with a password needs an email address.',
  'error.noWayToSignIn': 'That would leave them no way to sign in. Give them a password or a PIN.',
  'error.emailTaken': 'Someone already has that email address.',
  'error.lastAdmin':
    'This is the only administrator left. Make someone else an administrator first, or nobody can manage this kitchen.',
  'error.cannotRemoveSelf': 'You can’t remove your own account.',
  'error.onlyDishes': 'Only dishes go on a menu: {names}.',
  'error.menuNameTaken': 'There is already a menu with that name.',
  'error.menuUnavailable':
    'The allergens of {names} can’t be worked out, because it points at a recipe or ingredient that no longer exists. Fix the recipe first.',
  'error.recipeGone': 'That recipe no longer exists.',
  'error.recipeLoop':
    '{parent} can’t contain {child}, because {child} already contains {parent}. That would be a loop.',
  'error.scaleFailed': 'That recipe couldn’t be worked out at that size.',
  'error.scaleUnits': 'That size is in a different kind of unit from what this recipe makes.',
  'error.scaleLoop': 'This recipe contains itself, so it can’t be scaled.',
  'error.scaleNoPortions':
    'This recipe isn’t written in portions. Scale it by batch or by yield instead.',
  'error.stillUsed': 'Still used by {names}. Take it out of those first.',
  'error.alreadyInside': 'This device is on the kitchen network and needs no approval.',
  'error.allergensUnknown':
    'The allergens of this recipe can’t be worked out: something in it is missing or refers back to itself. Check what goes into it in the editor.',
  'error.nameOutsideKitchen':
    'The kitchen certificate only vouches for the kitchen network: addresses such as 192.168.1.50, and names ending in .local, .lan, .home or .internal. “{name}” isn’t one of them.',
  'error.invalidCredentials': 'That didn’t match. Try again.',
  'error.accountInactive': 'That account is no longer active.',
  'error.deviceNotEnrolled': 'PIN sign-in only works on a tablet an administrator has enrolled.',
  'error.notAuthenticated': 'Sign in to continue.',
  'error.forbidden': 'Your role doesn’t allow that.',
  'error.setupToken':
    'That setup code is wrong. Check the setup link printed when Mise was installed.',
  'error.accountLocked': 'Too many wrong attempts. Try again in 15 minutes.',
  'error.accountLockedUntil': 'Too many failed attempts. Try again after {time}.',

  'shell.unreachableTitle': 'Cannot reach the server',
  'shell.unreachableBody':
    'Check that the Mise server is running and that this device is on the same network.',
  'shell.home': 'Mise, today',
  'shell.signOut': 'Sign out',
  'shell.signOutFailed': 'Couldn’t sign out. Check the connection and try again.',
  'shell.sourceCode': 'Source code',
  'shell.language': 'Language',
  'shell.languageKitchen': 'Kitchen default ({language})',

  'nav.today': 'Today',
  'nav.recipes': 'Recipes',
  'nav.dishes': 'Dishes',
  'nav.ingredients': 'Ingredients',
  'nav.settings': 'Settings',
  'nav.updateAvailable': 'A newer Mise is out',

  'role.admin': 'Administrator',
  'role.head_chef': 'Head chef',
  'role.sous_chef': 'Sous chef',
  'role.cook': 'Cook',

  'recipeType.dish': 'Dish',
  'recipeType.prep': 'Prep',
  'recipeType.component': 'Component',

  'recipeStatus.draft': 'Draft',
  'recipeStatus.published': 'Published',
  'recipeStatus.archived': 'Archived',

  // The EU Annex II names, as a kitchen is inspected against them.
  'allergen.gluten': 'Cereals containing gluten',
  'allergen.crustaceans': 'Crustaceans',
  'allergen.eggs': 'Eggs',
  'allergen.fish': 'Fish',
  'allergen.peanuts': 'Peanuts',
  'allergen.soybeans': 'Soybeans',
  'allergen.milk': 'Milk',
  'allergen.nuts': 'Tree nuts',
  'allergen.celery': 'Celery',
  'allergen.mustard': 'Mustard',
  'allergen.sesame': 'Sesame',
  'allergen.sulphites': 'Sulphur dioxide and sulphites',
  'allergen.lupin': 'Lupin',
  'allergen.molluscs': 'Molluscs',

  'today.night': 'Night',
  'today.day': 'Day',
  'today.progress': '{done} of {total} done',
  'today.progressLabel': '{period} prep done',
  'today.mine': 'Mine',
  'today.all': 'All',
  'today.nothingMine': 'Nothing of yours on this list.',
  'today.nothingStation': 'Nothing on this station.',
  'today.pending_one': 'One tick hasn’t reached the server yet.',
  'today.pending_other': '{count} ticks haven’t reached the server yet.',
  'today.pendingStill': 'Retrying. They’re saved on this tablet until they go through.',
  'today.tickRefused': 'A tick didn’t go through: {reason}',

  'task.doneBy': 'Done by {name}',

  'line.loading': 'Loading the brigade…',
  'line.notEnrolledTitle': 'This tablet isn’t enrolled',
  'line.notEnrolledBody':
    'PIN sign-in only works on a tablet an administrator has set up. Ask them to sign in here and enrol it from Settings.',
  'line.signInPassword': 'Sign in with a password',
  'line.whoIsOn': 'Who’s on?',
  'line.nobodyHasPin':
    'Nobody has a PIN yet. An administrator can add one for each cook in Settings.',
  'line.notOnList': 'Not on the list, or on a laptop?',
  'line.signInEmail': 'Sign in with an email and password',
  'line.signingIn': 'Signing in',
  'line.checking': 'Checking…',
  'line.notMe': 'Not me',

  'login.title': 'Sign in to your kitchen',
  'login.subtitle': 'Chefs and managers, on any device.',
  'login.signIn': 'Sign in',
  'login.signingIn': 'Signing in…',
  'login.onTheLine': 'On the line?',
  'login.pinLink': 'Sign in with your PIN',
  'login.eyebrow': 'Email and password',
  'login.titleKitchen': 'Sign in to {kitchen}',
  'login.show': 'Show',
  'login.hide': 'Hide',
  'login.forgot': 'Forgotten your password? An administrator can set a new one under Settings.',
  'login.tabletBody': 'Cooks sign in on the kitchen tablet: tap your name, then your PIN.',
  'login.server': 'Connected to {host}',
  'line.eyebrow': 'Kitchen tablet',
  'line.tapNamePin': 'Tap your name, then your PIN.',
  'line.enterPin': 'Enter your PIN.',
  'line.digitsTyped_one': '{count} digit entered',
  'line.digitsTyped_other': '{count} digits entered',
  'line.welcome': 'Welcome, {name}.',
  'line.ok': 'OK',
  'line.clear': 'Clear',

  'setup.title': 'Set up this kitchen',
  'setup.body':
    'This server is empty. Name your kitchen and create the first administrator account. You will only see this page once.',
  'setup.kitchenName': 'Kitchen name',
  'setup.kitchenPlaceholder': 'The Ship Inn',
  'setup.yourName': 'Your name',
  'setup.yourNamePlaceholder': 'How the brigade knows you',
  'setup.passwordHint': 'At least 12 characters.',
  'setup.passwordShort': 'That’s still under 12 characters.',
  'setup.failed': 'Setup failed. Check the server log.',
  'setup.create': 'Create the kitchen',
  'setup.codeHint':
    'Setup from outside the kitchen network needs this code. It’s SETUP_TOKEN in Mise’s settings, or the end of the setup link the installer printed. If none was set, Mise made one up and wrote it in its log.',
  'setup.codeLabel': 'Setup code',
  'setup.stepKitchen': 'Restaurant',
  'setup.stepYou': 'You',
  'setup.stepBrigade': 'Stations & staff',
  'setup.stepRecipes': 'Recipes',
  'setup.stepSettings': 'Settings',
  'setup.stepCheck': 'Check',
  'setup.stepOf': 'Step {current} of {total}',
  'setup.stepShort': 'Step {current} / {total}',
  'setup.next': 'Next',
  'setup.back': 'Back',
  'setup.skip': 'Skip for now',
  'setup.optional': 'Optional',
  'setup.required': 'Fill this in first.',
  'setup.kitchenTitle': 'Name the restaurant',
  'setup.kitchenBody':
    'This name sits at the top of every screen. The language becomes the kitchen’s default; everyone can still choose their own.',
  'setup.youTitle': 'Your account',
  'setup.youBody':
    'You’re the administrator. You sign in with this email and password, on any device.',
  'setup.emailInvalid': 'That doesn’t look like an email address.',
  'setup.confirmPassword': 'Password again',
  'setup.showPassword': 'Show password',
  'setup.passwordMismatch': 'The two passwords don’t match.',
  'setup.passwordMatch': 'The passwords match.',
  'setup.brigadeTitle': 'Stations and staff',
  'setup.brigadeBody':
    'Stations group every prep list. Start with the ones you have; you can rename them or add more later.',
  'setup.stationsHeading': 'Stations',
  'setup.stationsEmpty': 'Add at least one station.',
  'setup.stationDuplicate': 'Two stations have the same name.',
  'setup.staffHeading': 'Staff',
  'setup.staffBody':
    'Cooks sign in on a kitchen tablet by tapping their name and entering their PIN. Managers can get an email and password later under Settings.',
  'setup.staffEmpty': 'Nobody else yet. Add your brigade now, or later under Settings.',
  'setup.addPerson': 'Add someone',
  'setup.nameMissing': 'Every row needs a name.',
  'setup.pinInvalid': 'A PIN is 4 to 8 digits.',
  'setup.yourPin': 'Your PIN (optional)',
  'setup.yourPinHint':
    'Lets you tap in on the kitchen tablet like the rest of the brigade. Leave it empty to skip; you can set one later.',
  'setup.summaryPin': 'PIN for the kitchen tablet',
  'setup.backupsTitle': 'Backups',
  'setup.backupsHint':
    'Mise backs up the whole kitchen every night. A copy somewhere else means a dead disk or a theft doesn’t take the backups too. You can change this later in Settings.',
  'setup.backupsTestFirst':
    'Test the connection to the NAS first, or choose another place for backups.',
  'setup.backupsConnectFirst': 'Connect OneDrive first, or choose another place for backups.',
  'setup.codeForBackups':
    'Needed to test the NAS or connect OneDrive from outside the kitchen network. It’s SETUP_TOKEN in Mise’s settings, or the code Mise wrote in its log.',
  'setup.recipesTitle': 'Recipes',
  'setup.recipesBody':
    'Put the dishes and prep you already know into the book: a name and what one batch makes. Ingredients and method can follow in the recipe book.',
  'setup.recipesEmpty': 'No recipes yet. You can skip this and add them in the recipe book later.',
  'setup.addRecipe': 'Add a recipe',
  'setup.recipeName': 'Recipe name',
  'setup.recipeType': 'Sort',
  'setup.makes': 'One batch makes',
  'setup.recipeDuplicate': 'Two recipes have the same name.',
  'setup.yieldInvalid': 'A batch has to make more than 0.',
  'setup.settingsTitle': 'How this kitchen works',
  'setup.settingsBody': 'All of this can be changed later under Settings.',
  'setup.thisDevice': 'This device',
  'setup.enrolDevice': 'Use this device as a kitchen tablet',
  'setup.enrolHint':
    'Staff can then sign in on it with their PIN. Leave this off if it is your own phone or laptop.',
  'setup.deviceName': 'Tablet name',
  'setup.checkTitle': 'Check and create',
  'setup.checkBody': 'Nothing is saved until you press create. Go back to any step to change it.',
  'setup.summaryNone': 'None',
  'setup.summaryAdmin': '{name} · administrator',
  'setup.summaryTablet': 'Enrolled as “{name}”',
  'setup.summaryNoTablet': 'Not a kitchen tablet',
  'setup.defaultLarder': 'Larder',
  'setup.defaultSauce': 'Sauce',
  'setup.defaultSaute': 'Sauté',
  'setup.defaultGrill': 'Grill',
  'setup.defaultPastry': 'Pastry',
  'welcome.step0': 'Welcome',
  'welcome.eyebrow': 'Fresh server · nothing set up yet',
  'welcome.headline': 'Your recipes and your prep, on your own server.',
  'welcome.lead1': 'This is a new Mise server. Setting up the kitchen takes about five minutes.',
  'welcome.lead2': 'Nothing is saved until the last step, and all of it can be changed later.',
  'welcome.f1t': 'Recipes as specs',
  'welcome.f1b':
    'Scale to a yield or a number of covers, build dishes from sub-recipes, and see the allergens of every sub-recipe.',
  'welcome.f2t': 'One prep list for the kitchen',
  'welcome.f2b':
    'Chefs put on what needs making, grouped by station. Ticked lines drop off by themselves two hours later.',
  'welcome.f3t': 'Cooks work from a tablet',
  'welcome.f3b':
    'Cooks tap a name and a PIN, tick prep off and claim a job, so nothing gets made twice. Every screen updates live.',
  'welcome.stepsLabel': 'Setup · six steps',
  'welcome.duration': 'About 5 minutes',
  'welcome.readyLabel': 'Have ready',
  'welcome.r1': "The restaurant's name",
  'welcome.r2': 'An email and a password for you, the administrator',
  'welcome.r3': 'Your stations — larder, sauce, pastry, pass',
  'welcome.r4': 'Optionally your staff, a 4–8 digit PIN each, and a few recipes',
  'welcome.changeNote': "Skip whatever you haven't got to hand. None of it is final.",
  'welcome.selfHost':
    'Everything you enter stays on this server. No account is made anywhere else, and Mise doesn’t send your data anywhere.',
  'welcome.cta': 'Set up this kitchen',
  'setup.readyLabel': 'Ready',
  'setup.readyEyebrow': 'Setup complete',
  'setup.readyTitle': '{kitchen} is ready.',
  'setup.readyBody': 'Opening the kitchen…',
  'tour.label': 'Tour',
  'tour.promptEyebrow': 'Welcome, {name}',
  'tour.promptTitle': 'Want a quick tour?',
  'tour.promptBody':
    'One minute: where your prep list is, how to tick things off and claim them, and where the recipes live.',
  'tour.promptNote': 'You will only be asked this once.',
  'tour.start': 'Show me around',
  'tour.notNow': 'Not now',
  'tour.skip': 'Skip tour',
  'tour.finish': 'Done',
  'tour.welcomeTitle': 'This is {kitchen}',
  'tour.welcomeBody':
    "Mise keeps the kitchen's recipes and prep in one place, on your own server. Here is where everything is.",
  'tour.todayBody':
    'Everything that needs making today, grouped by station. This is the page the line works from.',
  'tour.stationsTitle': 'Your station',
  'tour.stationsBody':
    'Narrow the list down to one station, or to just the jobs with your name on them.',
  'tour.tickTitle': 'Tick it off',
  'tour.tickBody': 'Tap a line when it is done. It greys out on every screen straight away.',
  'tour.makingBody':
    'Starting on something? Tap the small tick first, so nobody else makes the same thing.',
  'tour.addLineTitle': 'Add to the list',
  'tour.addLineBody':
    'Prep goes on the running list by hand. Add the same thing twice and it adds up on one line.',
  'tour.recipesBody':
    'Every recipe in the kitchen, with its allergens. Scale it to what you need, or print it for the pass.',
  'tour.ingredientsBody': 'What the recipes are built from, with allergens and trim loss.',
  'tour.settingsBody': 'Stations, staff and PINs, kitchen tablets, dish cards and backups.',
  'tour.accountTitle': 'Your language',
  'tour.accountBody': 'Choose your own language here, and sign out when your shift is done.',
  'tour.doneTitle': "That's the tour",
  'tour.doneBody': 'Everything else is in the bar at the top.',

  'task.remove': 'Take {task} off the list',
  'task.removeConfirm': 'Remove?',

  'addTask.what': 'What to do',
  'addTask.placeholder': 'Brunoise shallots',
  'addTask.submit': 'Add the line',
  'addTask.fromBook': 'From the book',
  'addTask.makes': 'makes {amount}',
  'addTask.linked':
    'Linked to the recipe {name}. Whoever makes it can open the spec at this amount.',
  'task.recipe': 'Recipe',
  'task.openRecipe': 'Open the recipe for {task}',

  'recipes.subtitle':
    'Preps and components. Use Free from to see what an allergy table can have, sub-recipes included. Dishes have their own tab.',
  'recipes.search': 'Search the book',
  'recipes.sort': 'Sort',
  'recipes.anySort': 'Any sort',
  'recipes.prep': 'Prep',
  'recipes.components': 'Components',
  'recipes.freeFrom': 'Free from…',
  'recipes.freeFromOption': 'Free from {allergen}',
  'recipes.clearFreeFrom': 'Stop filtering by allergen',
  'recipes.colSortStation': 'Sort · station',
  'recipes.colMakes': 'Makes',
  'recipes.noMatch': 'Nothing in the book matches.',
  'recipes.empty':
    'The book is empty. Start with a stock or a sauce, then build the dishes that use it.',
  'recipes.noStation': 'no station',
  'recipes.noneInTree': 'No {allergen}, sub-recipes included',
  'recipes.portions_one': '{count} portion',
  'recipes.portions_other': '{count} portions',
  'recipes.countFreeFrom': '{shown} of {total} recipes are free from {allergen}',
  'recipes.countFiltered': '{shown} of {total} recipes',
  'recipes.count_one': '{count} recipe in the book',
  'recipes.count_other': '{count} recipes in the book',
  'recipes.new': 'New recipe',

  'recipe.back': '← The book',
  'recipe.backToBook': 'Back to the book',
  'recipe.makes': 'Makes {amount}',
  'recipe.minutes': '{count} min',
  'recipe.keeps_one': 'Keeps {count} hour',
  'recipe.keeps_other': 'Keeps {count} hours',
  'recipe.asWritten': 'As written',
  'recipe.scaled': '{factor}× the written recipe',
  'recipe.contains': 'Contains',
  'recipe.nothingDeclarable': 'Nothing declarable',
  'recipe.mayContain': 'May contain {allergens}.',
  'recipe.workedOut': 'Worked out from the ingredients and every sub-recipe.',
  'recipe.empty': 'Nothing in this recipe yet.',
  'recipe.method': 'Method',
  'recipe.storage': 'Storage',
  'recipe.print': 'Print recipe card',
  'recipe.duplicate': 'Duplicate',
  'recipe.copyName': '{name} (copy)',
  'recipe.usedBy': 'Used by',
  'recipe.level': 'Lvl',
  'recipe.what': 'What',
  'recipe.open': 'open',

  'scale.ask': 'Ask for a size',
  'scale.portions': 'Portions',
  'scale.need': 'I need',
  'scale.batches': 'Batches',

  'plating.title': 'How it goes out',
  'plating.alt': 'How the plate goes out',
  'plating.remove': 'Remove photograph',
  'plating.empty': 'No photograph yet. The plate a cook copies goes here, straight on.',
  'plating.uploading': 'Uploading…',
  'plating.add': 'Add a plating photograph',
  'plating.addAnother': 'Add another photograph',

  'print.whatGoesIn': 'What goes in',

  'editor.editing': 'Editing',
  'editor.name': 'Recipe name',
  'editor.namePlaceholder': 'Chicken velouté',
  'editor.status': 'Status',
  'editor.lastSaved': 'Last saved {time}',
  'editor.notSaved': 'Not saved yet',
  'editor.colSort': 'What sort',
  'editor.colWhat': 'What it is',
  'editor.colNote': 'Note',
  'editor.sectionHeading': 'Section heading',
  'editor.removeHeading': 'Remove heading',
  'editor.newSection': 'Section {count}',
  'editor.notePlaceholder': 'finely diced',
  'editor.removeLine': 'Remove line',
  'editor.addLine': 'Add a line',
  'editor.lines_one': '{count} line',
  'editor.lines_other': '{count} lines',
  'editor.addSection': 'Add a section',
  'editor.hintNext': 'next line',
  'editor.hintField': 'next field',
  'editor.hintMove': 'move a line',
  'editor.hintCreate': 'on an unknown name creates it',
  'editor.kindRecipe': 'Recipe',
  'editor.kindIngredient': 'Ingredient',
  'editor.kindNew': 'New',
  'editor.theRest': 'The rest',
  'editor.batchMakes': 'One batch makes',
  'editor.dishPlates_one': 'Quantities are for one plate.',
  'editor.dishPlates_other': 'Quantities are for {count} plates.',
  'editor.yieldUnit': 'Yield unit',
  'editor.prepMinutes': 'Prep, minutes',
  'editor.keepsHours': 'Keeps, hours',
  'editor.storagePlaceholder': 'Vac-packed, walk-in, labelled',
  'editor.tags': 'Tags',
  'editor.tagsPlaceholder': 'pie, à la carte',
  'editor.mayContain': 'May contain · cross-contact only',
  'editor.rolledUp': 'Rolled up from the lines',
  'editor.rollupUnsaved': 'Save to work out the allergens.',
  'editor.rollupContains': 'Contains {allergens}',
  'editor.rollupNote': 'As of the last save, sub-recipes included.',
  'editor.rollupNone': 'Nothing declarable anywhere underneath, as last saved.',
  'editor.stepFirst': 'Sweat the shallots in butter without colour',
  'editor.stepNext': 'The next step',
  'editor.step': 'Step {count}',
  'editor.removeStep': 'Remove step {count}',

  'picker.label': 'What this line is',
  'picker.placeholder': 'Ingredient or sub-recipe',
  'picker.create': 'Create ingredient',
  'picker.creating': 'Creating',

  'ingredients.subtitle':
    'Trim yield, density and weight per piece let the prep list say how much to pull from the walk-in. You can also add ingredients straight from the recipe editor.',
  'ingredients.search': 'Search ingredients',
  'ingredients.new': 'New ingredient',
  'ingredients.colYield': 'Trim yield',
  'ingredients.colDensity': 'g / ml',
  'ingredients.colEach': 'g each',
  'ingredients.noMatch': 'Nothing matches.',
  'ingredients.empty': 'Nothing here yet.',
  'ingredients.explainTitle': 'What the three numbers are for',
  'ingredients.yieldTitle': 'Trim yield.',
  'ingredients.yieldBody':
    'What’s left after peeling and trimming, as a share of what was bought: a kilo of onions gives about 900 g you can use.',
  'ingredients.densityTitle': 'Grams per millilitre.',
  'ingredients.densityBody':
    'Lets a recipe written in litres pull a weight, and the other way round. Oil is about 0.92.',
  'ingredients.eachTitle': 'Grams each.',
  'ingredients.eachBody': 'Turns a count into a weight. An onion is about 170, an egg about 50.',
  'ingredients.byVolume': 'Measured by volume',
  'ingredients.counted': 'Counted',
  'ingredients.weighed': 'Weighed',
  'ingredients.category': 'Category',
  'ingredients.categoryPlaceholder': 'Vegetable',
  'ingredients.measured': 'Normally measured',
  'ingredients.optWeight': 'By weight',
  'ingredients.optVolume': 'By volume',
  'ingredients.optCount': 'By count',
  'ingredients.yieldLabel': 'Trim yield, per cent',
  'ingredients.yieldHint': 'What is left after peeling and trimming. Onions are about 90.',
  'ingredients.densityLabel': 'Grams per millilitre',
  'ingredients.densityHint': 'Only to convert between weight and volume. Oil is about 0.92.',
  'ingredients.eachLabel': 'Grams each',
  'ingredients.eachHint': 'Only to convert between count and weight. An egg is about 50.',
  'ingredients.allergens': 'Allergens',

  'settings.cookOnly': 'Only chefs and administrators can change how the kitchen is set up.',
  'settings.tabStaff': 'Staff',
  'settings.tabStations': 'Stations',
  'settings.tabDevices': 'Devices',
  'settings.tabKitchen': 'Kitchen',

  'staff.loadError': 'Could not load the staff list.',
  'staff.title': 'Staff',
  'staff.add': 'Add someone',
  'staff.you': '(you)',
  'staff.pinTag': 'PIN',
  'staff.passwordTag': 'Password',
  'staff.locked': 'Locked out',
  'staff.namePlaceholder': 'How the brigade knows them',
  'staff.role': 'Role',
  'staff.station': 'Usual station',
  'staff.noStation': 'No fixed station',
  'staff.newPin': 'New PIN',
  'staff.keepIt': 'Leave blank to keep it',
  'staff.pinPlaceholder': '4 digits',
  'staff.pinHint': 'For signing in on a kitchen tablet.',
  'staff.passwordSection': 'Password sign-in, for chefs and administrators',
  'staff.newPassword': 'New password',
  'staff.passwordPlaceholder': 'At least 12 characters',
  'staff.addSubmit': 'Add to the brigade',

  'stations.loading': 'Loading stations…',
  'stations.loadError': 'Could not load the stations.',
  'stations.title': 'Stations',
  'stations.body':
    'Prep lists are grouped by station and print in this order, so put them in the order the kitchen is laid out.',
  'stations.empty':
    'No stations yet. A kitchen needs at least one before a prep list can be grouped.',
  'stations.addLabel': 'Add a station',
  'stations.placeholder': 'Garde manger',
  'stations.rename': 'Rename',
  'stations.moveEarlier': 'Move {name} earlier',
  'stations.moveLater': 'Move {name} later',

  'devices.title': 'Kitchen tablets',
  'devices.body':
    'Cooks can only sign in with a PIN on an enrolled tablet. Enrol from the tablet itself, while signed in as an administrator on it.',
  'devices.enrolled': 'Enrolled {date}',
  'devices.lastUsed': 'last used {date}',
  'devices.neverUsed': 'never used',
  'devices.empty': 'No tablets enrolled yet, so nobody can sign in with a PIN.',
  'devices.enrolLabel': 'Enrol the device you’re on now',
  'devices.placeholder': 'Pass tablet',
  'devices.enrolling': 'Enrolling…',
  'devices.enrol': 'Enrol this device',
  'devices.enrolledOk': 'Enrolled. This device can now show the staff roster and take PINs.',
  'devices.revokedTitle': 'Revoked',
  'devices.revokedOn': 'revoked {date}',
  'devices.revoke': 'Revoke',
  'devices.reallyRevoke': 'Really revoke?',
  'devices.revokeTitle': 'Signs everyone out of {name} immediately',

  'kitchen.loadError': 'Could not load the kitchen settings.',
  'version.title': 'Mise version',
  'version.running': 'This kitchen runs Mise {version}.',
  'version.upToDate': 'That’s the newest release.',
  'version.newer': 'Mise {version} is out.',
  'version.whatChanged': 'What changed',
  'version.checkFailed':
    'Couldn’t reach GitHub to check for a newer version. Mise tries again tomorrow.',
  'version.notChecked': 'Not checked yet. Mise looks a minute after it starts, then once a day.',
  'version.checkLabel': 'Check for a newer version once a day',
  'version.checkHint':
    'Mise asks GitHub which release is the newest. Nothing about your kitchen is sent.',
  'version.howTitle': 'How to update',
  'version.backupFirst': 'Download a backup first. The database updates itself when Mise starts.',
  'version.major':
    '{version} is a new major version. Read what changed first, and set MISE_VERSION to {major} in .env, or the image tag to :{major} on TrueNAS.',
  'version.howTrueNas': 'TrueNAS',
  'version.howTrueNasSteps': 'Open Apps, choose the mise app and click Update or Pull Images.',
  'version.howInstaller': 'Installed with the installer',
  'version.howInstallerSteps': 'In the mise-kitchen folder, run the install command again:',
  'version.howDocker': 'Plain Docker',
  'version.howDockerSteps': 'In the folder with compose.yaml, run:',
  'kitchen.title': 'This kitchen',
  'kitchen.units': 'Units',
  'kitchen.metric': 'Metric — grams and litres',
  'kitchen.imperial': 'Imperial — ounces and US cups',
  'kitchen.unitsHint':
    'Which units new recipes default to. Existing recipes keep whatever they were written in.',
  'kitchen.language': 'Language',
  'kitchen.languageHint':
    'What the interface speaks for anyone who has not chosen their own. Recipes stay exactly as they were typed.',
  'kitchen.retention': 'Lines gone from the running list',
  'kitchen.retentionKeep': 'Keep them in the database, out of sight',
  'kitchen.retentionDelete': 'Delete them to save space',
  'kitchen.retentionHint':
    'Kept lines are a record of who did what and when, for later reports, and take up storage. Deleted lines are gone for good.',

  'handover.title': 'Handover',
  'handover.empty': 'Leave a note for the next shift',
  'handover.placeholder': 'Fish delivery is late; stock on at three.',
  'handover.save': 'Save the note',
  'handover.clear': 'Clear the note',
  'handover.edit': 'Change',
  'handover.remove': 'Remove the note',
  'handover.removeConfirm': 'Remove?',
  'handover.by': '{name}, {when}',
  'running.title': 'The list',
  'running.subtitle':
    'One list for the whole kitchen. Ticked lines go two hours after they are ticked.',
  'running.empty': 'Nothing on the list. Add what needs doing.',
  'running.merged': 'Added to the {name} already on the list.',
  'running.goneAt': 'gone at {time}',

  'task.beingMade': 'Being made',
  'task.beingMadeBy': 'Being made by {name}',
  'settings.tabBackups': 'Backups',
  'backups.title': 'Backups',
  'backups.body':
    'Every night Mise saves the whole kitchen, photos included, in one encrypted file. Keep a copy somewhere other than this machine as well.',
  'backups.lastGood': 'Last backup: {time}',
  'backups.never': 'No backup yet. The first one runs within a few minutes.',
  'backups.failed': 'The last backup failed.',
  'backups.reason.bad_key': 'A recovery key is 32 letters and numbers, in groups of four.',
  'backups.reason.wrong_key': "That recovery key doesn't open this backup, or the file is damaged.",
  'backups.reason.not_a_backup': "That file isn't a Mise backup.",
  'backups.reason.too_new':
    'That backup comes from a newer version of Mise. Update this server first.',
  'backups.reason.local': "It couldn't be saved on this machine. The disk may be full.",
  'backups.reason.unreachable':
    "Mise couldn't reach the NAS. Check the address and that the NAS is switched on.",
  'backups.reason.auth': 'The NAS turned the username or password down.',
  'backups.reason.folder': "That folder doesn't exist on the NAS. Create it there first.",
  'backups.reason.remote': 'The NAS refused to store the file.',
  'backups.reason.unknown': 'Something unexpected went wrong. The server log has the details.',
  'backups.running': 'Backing up…',
  'backups.now': 'Back up now',
  'backups.download': 'Download',
  'backups.keyTitle': 'Recovery key',
  'backups.keyBody':
    'A backup only opens with this key. Print it or write it down, and keep it somewhere other than this machine. Without it, nobody can open a backup.',
  'backups.keyShow': 'Show recovery key',
  'backups.keyHide': 'Hide',
  'backups.keyPrint': 'Print',
  'backups.keyConfirm': "I've written it down",
  'backups.keyConfirmed': 'Marked as written down.',
  'backups.keyPrintTitle': 'Recovery key for {kitchen}',
  'backups.keyPrintBody':
    'You need this key to restore a backup of this kitchen in Mise. Keep it away from the server, in the office safe or at home.',
  'backups.whereTitle': 'Where backups go',
  'backups.onlyHereTitle': 'Backups only live on this machine',
  'backups.onlyHereBody':
    'If it breaks, is stolen or its disk fails, the backups go with it. Send them to a NAS or OneDrive as well.',
  'backups.onlyHereAction': 'Choose where they go',
  'backups.whereLocal': 'On this machine, always',
  'backups.whereLocalHint':
    'These are kept on the same machine as Mise. If it breaks, is stolen or its disk fails, they go with it, so download one now and then or send them to a NAS or OneDrive.',
  'backups.whereWebdav': 'Also to a NAS or Nextcloud',
  'backups.whereWebdavHint':
    'Over WebDAV, which Synology, QNAP and Nextcloud all offer. A copy stays on this machine too.',
  'backups.webdavUrl': 'Folder address',
  'backups.webdavUrlHint':
    'The WebDAV address of a folder that already exists. Plain http:// is fine inside the restaurant.',
  'backups.webdavUser': 'Username',
  'backups.webdavPassword': 'Password',
  'backups.webdavPasswordKept': 'Leave empty to keep the saved password.',
  'backups.test': 'Test the connection',
  'backups.testing': 'Testing…',
  'backups.testOk': 'Connected. Mise can write to that folder.',
  'backups.keepRemote': 'Keep on the NAS',
  'backups.keepLocal': 'Keep on this machine',
  'backups.copies_one': '{count} backup',
  'backups.copies_other': '{count} backups',
  'backups.when': 'Nightly backup at',
  'backups.zone': 'Time zone: {zone}.',
  'backups.kept': 'Kept on this machine',
  'backups.keptEmpty': 'Nothing kept here yet.',
  'backups.bannerFailed': 'The last backup failed.',
  'backups.bannerStale': "There hasn't been a backup for two days.",
  'backups.bannerKey':
    'Nobody has written the backup recovery key down yet. Without it, no backup can be opened.',
  'backups.bannerAction': 'Open backups',
  'backups.whereOnedrive': 'Also to OneDrive',
  'backups.whereOnedriveHint':
    "Into the Apps/Mise folder of your OneDrive. Mise can't see anything else in it. A copy stays on this machine too.",
  'backups.onedriveConnect': 'Connect OneDrive',
  'backups.onedriveStarting': 'Asking Microsoft…',
  'backups.onedriveCodeIntro':
    'On any phone or computer, open this page, enter the code below, and sign in with the Microsoft account whose OneDrive should get the backups.',
  'backups.onedriveWaiting': 'Waiting for the sign-in…',
  'backups.onedriveUnverified':
    "Microsoft will call Mise unverified. That's normal for independent apps, and Mise only asks for its own folder.",
  'backups.onedriveConnected': 'Connected to the OneDrive of {account}.',
  'backups.onedriveDisconnect': 'Disconnect',
  'backups.onedrive.declined': 'The sign-in was turned down at Microsoft.',
  'backups.onedrive.expired': 'The code ran out before anyone signed in. Start again.',
  'backups.onedrive.failed': "Microsoft didn't accept the connection. Try again in a minute.",
  'backups.keepOnedrive': 'Keep on OneDrive',
  'backups.keepBoth': 'Keep on the NAS and on OneDrive',
  'backups.reason.onedrive_auth':
    'OneDrive no longer lets Mise in. Connect it again on the backups page.',
  'backups.reason.onedrive_full': 'OneDrive is full.',
  'backups.reason.onedrive_unreachable':
    "Mise couldn't reach OneDrive. Check the internet connection.",
  'backups.reason.onedrive_failed': 'OneDrive refused the backup. Mise tries again in a few hours.',
  'restore.cta': 'Restore a backup',
  'restore.eyebrow': 'Restore',
  'restore.title': 'Bring a kitchen back',
  'restore.body':
    'Choose a backup file and type in the recovery key that was printed for it. Staff, recipes, photos and prep lists all come back. Everyone signs in again afterwards.',
  'restore.file': 'Backup file',
  'restore.key': 'Recovery key',
  'restore.keyHint': 'Capitals and dashes are optional.',
  'restore.fileMissing': 'Choose a backup file.',
  'restore.keyMissing': 'Type in the recovery key.',
  'restore.submit': 'Restore',
  'restore.restoring': 'Restoring…',
  'restore.working': 'On a small machine this can take a minute.',
  'restore.done': '{kitchen} is back',
  'restore.doneBody':
    'Sign in with the same email and password as before. Backups stay on this machine until you set up a NAS again in Settings.',
  'restore.counts': 'Staff: {staff} · Recipes: {recipes} · Photos: {photos}',
  'restore.signIn': 'Sign in',
  'restore.asideTitle': 'What comes back',
  'restore.asideStaff': 'Staff, with their passwords and PINs',
  'restore.asideRecipes': 'Recipes, ingredients and plating photos',
  'restore.asidePrep': 'The prep list',
  'restore.asideTablets': 'Enrolled tablets, as long as the server keeps its address',
  // Dish cards
  'recipe.dishCard': 'Dish card',
  'editor.frontOfHouse': 'Front of house',
  'editor.frontOfHouseHint': 'What the dish card tells the floor.',
  'editor.flavour': 'Flavour profile',
  'editor.flavourPlaceholder': 'creamy, garlic, fresh',
  'editor.temperature': 'Served',
  'editor.course': 'Place on the menu',
  'editor.drinks': 'Drink pairings',
  'editor.drinksPlaceholder': 'white Bordeaux, kombucha',
  'editor.cutlery': 'Cutlery',
  'editor.cutleryPlaceholder': 'small spoon',
  'editor.listHint': 'Separate with commas.',
  'editor.notSet': 'Not set',
  'temperature.cold': 'Cold',
  'temperature.room': 'Room temperature',
  'temperature.warm': 'Warm',
  'temperature.hot': 'Hot',
  'temperature.hot_and_cold': 'Hot and cold',
  'course.amuse': 'Amuse-bouche',
  'course.starter': 'Starter',
  'course.middle': 'Middle course',
  'course.main': 'Main course',
  'course.pre_dessert': 'Pre-dessert',
  'course.dessert': 'Dessert',
  'course.mignardise': 'Mignardises',
  'course.side': 'Side dish',
  'ingredients.vegan': 'Vegan',
  'ingredients.veganYes': 'Yes',
  'ingredients.veganNo': 'No',
  'ingredients.veganUnknown': 'Not said',
  'ingredients.veganHint':
    'A dish card never calls a dish vegan while one of its ingredients is not said.',
  'ingredients.whichCereals': 'Which cereals',
  'ingredients.whichNuts': 'Which nuts',
  'ingredients.cerealsPlaceholder': 'wheat, rye',
  'ingredients.nutsPlaceholder': 'hazelnut, walnut',
  'ingredients.detailsHint': 'The law wants the cereal and the nut named on a dish card.',
  'card.title': 'Dish card',
  'card.back': '← The dish',
  'card.language': 'Card language',
  'card.theme': 'Look',
  'card.themeDay': 'Day',
  'card.themeNight': 'Night',
  'card.print': 'Print',
  'card.download': 'Download PDF',
  'card.downloading': 'Making the PDF…',
  'card.downloadFailed': 'The PDF could not be made. Try again, or print instead.',
  'card.notADish': 'Only a dish has a dish card.',
  'card.unavailable':
    'This dish points at a recipe or ingredient that no longer exists, so its allergens can’t be worked out. Fix the recipe first.',
  'card.noAccess': 'Only chefs and administrators can open dish cards in this kitchen.',
  'card.attentionTitle': 'Worth checking before you print',
  'card.attentionVegan':
    'Nobody has said whether these are vegan: {names}. Until someone does, the card can’t call the dish vegan.',
  'card.attentionDetails': 'These contain gluten or nuts without saying which: {names}.',
  'card.attentionPhoto': 'This dish has no photo yet.',
  'card.attentionDraft': 'This recipe is still a draft.',
  'card.fixIngredients': 'Open the ingredients',
  'card.menu': 'Menu',
  'card.flavour': 'Flavour profile',
  'card.temperature': 'Temperature',
  'card.course': 'Course',
  'card.components': 'Components',
  'card.drinks': 'Drink pairings',
  'card.cutlery': 'Cutlery',
  'card.allergens': 'Allergens',
  'card.component': 'Component',
  'card.vegan': 'Vegan',
  'card.other': 'Other ingredients in the dish',
  'card.keyContains': 'contains',
  'card.keyMayContain': 'may contain traces',
  'card.keyVegan': 'vegan',
  'card.keyUnknown': 'not filled in',
  'card.cerealsAndNuts': 'Gluten cereals and nuts',
  'card.noCerealsOrNuts': 'No gluten cereals or nuts in this dish.',
  'card.cereals': 'Cereals: {names}',
  'card.nuts': 'Nuts: {names}',
  'card.unnamed': 'not named yet',
  'card.printed': 'Printed {date}',
  'card.page': 'Page {page} of {pages}',
  'card.madeWithMise': 'Made with Mise, free software under the AGPL-3.0',
  'card.noAllergens': 'None of the 14 allergens',
  'card.notVegan': 'Not vegan',
  'card.veganUnknown': 'Vegan: not filled in',
  'cardAllergen.eggs': 'Egg',
  'cardAllergen.gluten': 'Gluten',
  'cardAllergen.lupin': 'Lupin',
  'cardAllergen.milk': 'Milk',
  'cardAllergen.mustard': 'Mustard',
  'cardAllergen.sulphites': 'Sul\u00adphites',
  'cardAllergen.molluscs': 'Mol\u00adluscs',
  'cardAllergen.fish': 'Fish',
  'cardAllergen.soybeans': 'Soy',
  'cardAllergen.sesame': 'Sesame',
  'cardAllergen.celery': 'Celery',
  'cardAllergen.crustaceans': 'Crusta\u00adceans',
  'cardAllergen.peanuts': 'Peanuts',
  'cardAllergen.nuts': 'Tree nuts',
  'settings.tabDishCards': 'Dish cards',
  'dishCards.intro': 'How every dish card in this kitchen looks. Open a card from any dish.',
  'dishCards.managersOnly': 'Only chefs and administrators can change how dish cards look.',
  'dishCards.titleLabel': 'Heading',
  'dishCards.titleHint': 'Leave empty for “{default}”.',
  'dishCards.locationName': 'Location name',
  'dishCards.address': 'Address',
  'dishCards.logo': 'Logo',
  'dishCards.logoHint': 'JPEG, PNG or WebP, up to 2 MB. Square works best.',
  'dishCards.logoUpload': 'Choose a logo',
  'dishCards.logoReplace': 'Replace',
  'dishCards.logoRemove': 'Remove',
  'dishCards.logoNone': 'No logo yet.',
  'dishCards.warning': 'Warning at the foot of every card',
  'dishCards.warningHint':
    'Write it in each language you print in. A card in a language without one uses the warning in the kitchen’s language.',
  'dishCards.warningPlaceholder': 'This dish card is the property of …',
  'dishCards.show': 'On the card',
  'dishCards.part.menu': 'Menu',
  'dishCards.part.flavour': 'Flavour profile',
  'dishCards.part.temperature': 'Temperature',
  'dishCards.part.course': 'Place on the menu',
  'dishCards.part.drinks': 'Drink pairings',
  'dishCards.part.cutlery': 'Cutlery',
  'dishCards.part.photo': 'Photo',
  'dishCards.part.logo': 'Logo',
  'dishCards.part.location': 'Location and address',
  'dishCards.part.summary': 'Allergens on the front',
  'dishCards.part.mayContain': '“May contain” marks',
  'dishCards.part.vegan': 'Vegan column',
  'dishCards.theme': 'Look',
  'dishCards.themeDayHint': 'White, light on toner.',
  'dishCards.themeNightHint': 'Dark, best on screen or a colour laser.',
  'dishCards.access': 'Who can open dish cards',
  'dishCards.accessEveryone': 'Everyone signed in',
  'dishCards.accessManagers': 'Chefs and administrators only',
  'dishCards.miseLine': 'Every card ends with a small line saying it was made with Mise.',
  // Menus
  'nav.menus': 'Menus',
  'dishes.subtitle':
    'Every dish as its card: what the floor reads, with the allergens worked out from every sub-recipe.',
  'dishes.new': 'New dish',
  'dishes.search': 'Find a dish',
  'dishes.anyCourse': 'Any course',
  'dishes.onMenu': 'Only on an active menu',
  'dishes.noMatch': 'No dish matches.',
  'dishes.empty':
    'No dishes yet. A dish is what goes out to the pass: add one and it shows here as its card.',
  'dishes.count_one': '{count} dish',
  'dishes.count_other': '{count} dishes',
  'dishes.countFreeFrom': '{shown} of {total} dishes are free from {allergen}',
  'dishes.countFiltered': '{shown} of {total} dishes',
  'dishes.notOnMenu': 'Not on a menu',
  'dishes.broken': 'The allergens can’t be worked out: a sub-recipe is missing or uses itself.',
  'dishes.openSpec': 'Recipe',
  'dishes.print': 'Print or PDF',
  'dishes.kept':
    'This kitchen keeps dish cards to chefs and administrators. The recipe is still open to you.',
  'menus.subtitle':
    'The dishes front of house sells together. Print a menu’s allergen overview, or all its dish cards at once.',
  'menus.name': 'Menu name',
  'menus.namePlaceholder': 'Autumn menu 2026',
  'menus.new': 'New menu',
  'menus.empty': 'No menus yet. Start one with a name above.',
  'menus.dishes_one': '{count} dish',
  'menus.dishes_other': '{count} dishes',
  'menuStatus.draft': 'Draft',
  'menuStatus.active': 'Active',
  'menuStatus.archived': 'Archived',
  'menus.back': '← Menus',
  'menus.dishesTitle': 'On the menu',
  'menus.changedCount_one': '{count} changed since the cards were printed',
  'menus.changedCount_other': '{count} changed since the cards were printed',
  'menus.noDishes': 'No dishes on this menu yet.',
  'menus.noCourse': 'No course',
  'menus.changed': 'Changed',
  'menus.removeDish': 'Take {name} off the menu',
  'menus.addDish': 'Add the dish',
  'menus.pickDish': 'Choose a dish',
  'menus.allDishesOn': 'Every dish is already on this menu.',
  'menus.status': 'Status',
  'menus.description': 'Description',
  'menus.descriptionPlaceholder': 'A few words for the floor, such as the price or when it runs.',
  'menus.overview': 'Allergen overview',
  'menus.cardsTitle': 'Dish cards',
  'menus.cardsProgress': 'Making card {done} of {total}…',
  'menus.allCards': 'All dish cards (PDF)',
  'menus.lastPrinted': 'Cards last downloaded {when}.',
  'menus.neverPrinted': 'The dish cards of this menu haven’t been downloaded yet.',
  'menus.copyName': 'Name of the copy',
  'menus.copyPlaceholder': '{name} (copy)',
  'menus.duplicate': 'Copy the menu',
  'menus.delete': 'Delete the menu',
  'menus.backToMenu': '← The menu',
  'menus.overviewTitle': 'Allergen overview',
  'menus.dish': 'Dish',
  'access.eyebrow': 'Outside the kitchen',
  'access.title': 'Ask for access',
  'access.body':
    'From outside the kitchen, Mise only opens on devices the kitchen has approved. Give this device a name and ask. A chef or the administrator approves it in Settings → Devices.',
  'access.nameLabel': 'Name for this device',
  'access.namePlaceholder': 'e.g. Sam’s phone',
  'access.ask': 'Ask for access',
  'access.asking': 'Asking…',
  'access.waitingTitle': 'Waiting for approval',
  'access.waitingBody':
    '“{name}” has asked. Ask a chef or the administrator to approve it in Settings → Devices, and read them this code so they know it’s you:',
  'access.codeLabel': 'Code',
  'access.checking': 'This page opens Mise by itself once it’s approved.',
  'access.whyTitle': 'Why ask?',
  'access.whyBody':
    'Recipes, staff and allergen records belong to the kitchen. Asking first means nobody on the internet can even reach the sign-in page. Approval lasts until it’s taken back, or until this device goes 90 days unused.',
  'outside.title': 'Outside the kitchen',
  'outside.body':
    'Phones and laptops outside the kitchen network or VPN. They reach nothing but a request page until someone approves them here. An approval lasts until you take it back, or until the device goes 90 days unused.',
  'outside.waiting': 'Waiting for approval',
  'outside.approved': 'Approved',
  'outside.empty':
    'No outside devices. Someone who opens Mise from outside the kitchen can ask for access, and their request appears here.',
  'outside.code': 'Code',
  'outside.checkCode': 'Approve only if the person asking sees this same code on their screen.',
  'outside.passwordLabel': 'Your password',
  'outside.passwordWhy': 'You’re outside the kitchen, so approving asks for your password again.',
  'outside.approve': 'Approve',
  'outside.deny': 'Turn down',
  'outside.approvedBy': 'approved by {name} on {date}',
  'outside.approvedByFrom': 'approved by {name} from {device} on {date}',
  'outside.approvedOn': 'approved on {date}',
  'outside.thisDevice': 'This device',
  'outside.revokeSelf': 'This device will need to ask again',
  'outside.reallyRevokeSelf': 'Lock this device out?',
  'error.accessRequired': 'This device isn’t approved for access from outside the kitchen.',
  'error.wrongPassword': 'That password isn’t right.',
  'error.passwordNeeded':
    'Approving from outside needs a password, and this account has none. Approve it from inside the kitchen.',
  'error.tooManyRequests': 'Too many devices are waiting to be approved. Try again later.',
  'settings.tabConnection': 'Connection',
  'connection.relayedTitle': 'Mise can’t tell who is inside the kitchen',
  'connection.relayedBody':
    'Some visitors reach Mise through Docker’s own relay ({address}), which hides where they really are. So Mise counts them as outside the kitchen: each device asks for access once, and a chef approves it.',
  'connection.relayedTrusted':
    'Some visitors reach Mise through Docker’s own relay ({address}), which hides where they really are. You chose to count them as inside the kitchen, so they skip the approval outside devices need, wherever they are.',
  'connection.relayedTrust': 'Count these visitors as inside the kitchen',
  'connection.relayedTrustHint':
    'Only when Mise can’t be reached from the internet, or only through a web server you trust. Otherwise anyone who finds it skips the approval.',
  'connection.relayedFix':
    'This happens with rootless Docker, Podman, Docker Desktop, or over IPv6 when Docker’s network has none. Run Mise with host networking (network_mode: host), or turn on IPv6 for Docker’s network, so Mise sees each visitor’s own address. See “From outside the kitchen” in the README.',
  'connection.domainTitle': 'Address from outside',
  'connection.domainBody':
    'If Mise can be reached over the internet, enter the address that leads to it, such as mise.yourkitchen.com. From outside the kitchen, Mise then answers only on that address: the bare IP, or another website’s name on the same server, finds nothing. On the kitchen network and VPN, every address keeps working.',
  'connection.domainLabel': 'Domain',
  'connection.domainSet': 'From outside, Mise answers only on {domain}.',
  'connection.domainUnset':
    'Not set: from outside, any address that reaches this server leads to Mise.',
  'connection.domainClear': 'Clear',
  'connection.domainUseCurrent': 'Use {domain}, the address you’re on now',
  'connection.domainShared':
    'Other websites on the same public IP? Whatever already serves them (Apache, LiteSpeed or Nginx, often set up through a panel like cPanel or Plesk) has to pass this domain on to Mise, keeping the domain name in the request. If Mise runs on a different connection from those sites, nothing else is needed.',
  'error.domainLocksYouOut':
    'You’re connected through a different address, so this would lock you out. Set it from inside the kitchen, or open Mise through the new address first.',
  'proxy.title': 'Behind another web server',
  'proxy.body':
    'Does another web server on your network (Apache, say, with your websites) already answer for your domains? Let it pass your Mise domain on, with its own certificate. Mise then has to know which machine that is, so it can tell who is at home and who is outside.',
  'proxy.trusted': 'Mise takes {address} at its word about who each visitor is.',
  'proxy.none': 'No web server trusted: anything passed on by another machine counts as outside.',
  'proxy.stop': 'Stop trusting',
  'proxy.seen': 'Requests for {host} are arriving through {address}. Is that your web server?',
  'proxy.trust': 'Trust {address}',
  'proxy.addressLabel': 'Or type the web server’s address',
  'proxy.trustTyped': 'Trust',
  'proxy.apacheTitle': 'Set up Apache',
  'proxy.miseAddress': 'Mise on your network',
  'proxy.step1': 'On the Apache machine, check it can reach Mise. This should print "ok":',
  'proxy.step1Bridge':
    'Apache in a VM on the same TrueNAS as Mise, and nothing comes back? TrueNAS VMs often can’t reach their own host. Create a bridge (br0) under Network in TrueNAS, move the host’s address onto it, and attach the VM’s network card to br0.',
  'proxy.step2': 'Save this as /etc/apache2/sites-available/mise.conf:',
  'proxy.step3': 'Turn it on and get its certificate, the same way as your other sites:',
  'proxy.step4':
    'Open https://{domain} once, then come back here: Mise shows the address it came through, and you trust it.',
  'proxy.router':
    'Your router doesn’t change: ports 80 and 443 already go to Apache. Point the domain at your home IP, like your other sites.',
  'proxy.copy': 'Copy',
  'proxy.copied': 'Copied',
  'error.proxyNotLocal':
    'Use the address of a web server on the kitchen network, such as 192.168.1.60.',
  'https.title': 'HTTPS',
  'https.body':
    'Plain HTTP keeps working throughout. HTTPS is what lets tablets keep working offline and install Mise as an app, and it’s a must for anything reachable from the internet.',
  'https.on': 'On',
  'https.off': 'Off',
  'https.kitchenTitle': 'On the kitchen network',
  'https.kitchenBody':
    'Mise makes a certificate of its own for the addresses your tablets use. No domain or internet needed. Each tablet trusts it once, from the trust page.',
  'https.renews': 'Renews by itself. The current one lasts until {date}.',
  'https.trustEachTablet': 'On each tablet, open this page once and follow the steps:',
  'https.namesLabel': 'Addresses the tablets use',
  'https.namesHint':
    'The address in the tablets’ address bar, without http:// or a port. Several? Separate them with commas.',
  'https.making': 'Making…',
  'https.turnOn': 'Turn on',
  'https.turnOff': 'Turn off',
  'https.updateNames': 'Update addresses',
  'https.portNote':
    'HTTPS answers on port {port}. On TrueNAS, the app needs that port published too.',
  'https.domainTitle': 'For your domain',
  'https.domainTitleFor': 'For {domain}',
  'https.domainBody':
    'A free certificate from Let’s Encrypt, which every browser trusts with no steps on the device. Mise gets it and renews it by itself.',
  'https.domainNeedsAddress': 'Set the address from outside above first.',
  'https.domainBehindProxy':
    'Mise is behind your own web server, which handles HTTPS for this domain. You don’t need this.',
  'https.domainStep1': '{domain} points at your public IP address.',
  'https.domainStep2':
    'Your router passes port 80 on to Mise’s port {http}, and port 443 to Mise’s port {https}. On a cloud server, the install does this.',
  'https.domainStep3': 'Then ask for the certificate:',
  'https.getCertificate': 'Get certificate',
  'https.asking': 'Asking Let’s Encrypt…',
  'https.askingNote': 'This usually takes a few seconds, at most two minutes.',
  'https.failedHint':
    'Most often port 80 isn’t reaching Mise yet, or the domain doesn’t point here yet. Let’s Encrypt allows only a few failed tries an hour.',
  'https.renewFailed': 'The last renewal failed, and Mise will try again: {reason}',
  'trust.eyebrow': 'This device',
  'trust.title': 'Trust the kitchen certificate',
  'trust.body':
    'Once per device. Afterwards Mise opens over HTTPS here, keeps working when the network drops, and can be installed as an app.',
  'trust.notOn': 'HTTPS isn’t on yet. An administrator turns it on in Settings → Connection.',
  'trust.trusted': 'This device trusts it',
  'trust.trustedBody': 'You’re on Mise over HTTPS. Keep using this address from now on.',
  'trust.openMise': 'Open Mise',
  'trust.system.ios': 'iPad / iPhone',
  'trust.system.android': 'Android',
  'trust.system.windows': 'Windows',
  'trust.system.mac': 'Mac',
  'trust.system.other': 'Other',
  'trust.download': 'Download the kitchen certificate.',
  'trust.downloadIos':
    'In Safari, download the kitchen certificate and allow it when asked about a configuration profile.',
  'trust.downloadButton': 'Download certificate',
  'trust.ios1':
    'Open Settings, tap “Profile Downloaded” near the top, then Install, and enter your passcode.',
  'trust.ios2': 'Go to Settings → General → About → Certificate Trust Settings.',
  'trust.ios3': 'Turn on full trust for “Mise”.',
  'trust.android1':
    'Open Settings and search for “CA certificate” (often under Security → Encryption & credentials → Install a certificate).',
  'trust.android2':
    'Choose “CA certificate”, tap “Install anyway”, and pick mise-kitchen.crt from your downloads.',
  'trust.windows1': 'Open the downloaded file, choose “Install Certificate”, then “Current User”.',
  'trust.windows2':
    'Choose “Place all certificates in the following store”, pick “Trusted Root Certification Authorities”, finish, and restart the browser.',
  'trust.mac1': 'Open the downloaded file. Keychain Access adds it to your login keychain.',
  'trust.mac2':
    'Double-click “Mise”, open Trust, and set “When using this certificate” to “Always Trust”.',
  'trust.other1':
    'Add the downloaded file to the system’s or browser’s trusted certificate authorities. Firefox keeps its own list: Settings → Privacy & Security → Certificates → Authorities → Import.',
  'trust.check':
    'Check it worked: this opens this page over HTTPS. A warning instead means a step was missed.',
  'trust.checkButton': 'Check',
  'trust.fingerprint': 'Fingerprint, to compare',
  'error.badName':
    'That is not an address or host name. Use something like 192.168.1.50 or mise.local.',
  'error.noDomain': 'Set the address from outside first.',
  'offline.title': 'Offline.',
  'offline.body':
    'Mise can’t be reached since {time}. This shows what this device had, and changes are kept here until Mise answers again.',
  'offline.waiting_one': '1 change is waiting.',
  'offline.waiting_other': '{count} changes are waiting.',
  'offline.sending_one': 'Sending 1 change made while offline…',
  'offline.sending_other': 'Sending {count} changes made while offline…',
  'offline.dropped': '{what} couldn’t be saved: {reason}',
  'offline.setAside':
    '{what} couldn’t be saved: Mise ran into a problem with it. Other changes go on.',
  'offline.tryAgain': 'Try again',
  'offline.letGo': 'Let it go',
  'offline.lineWaiting': 'Waiting to be sent',
  'offline.allergensOnceSent': 'Allergens update once your changes are sent.',
  'offline.cardOnceSent': 'This card updates once your changes are sent.',
  'offline.menuOnceSent': 'Allergens and dish cards update once your changes are sent.',
  'offline.conflictTitle': 'Changed on another device',
  'offline.conflictBody':
    'While you were offline, someone else changed {what}. Which version should stay?',
  'offline.yours': 'Yours',
  'offline.theirs': 'Now in Mise',
  'offline.keepMine': 'Keep mine',
  'offline.keepTheirs': 'Keep what’s in Mise',
  'offline.items_one': '1 item',
  'offline.items_other': '{count} items',
  'offline.field.note': 'Note',
  'offline.field.notes': 'Notes',
  'offline.field.method': 'Method',
  'offline.field.description': 'Description',
  'offline.field.tags': 'Tags',
  'offline.field.components': 'Components',
  'offline.field.dishes': 'Dishes',
  'offline.field.type': 'Kind of recipe',
  'offline.field.yield': 'Makes',
  'offline.field.portions': 'Portions',
  'offline.field.prepMinutes': 'Prep time',
  'offline.field.cookMinutes': 'Cooking time',
  'offline.field.keeps': 'Keeps',
  'offline.field.allergens': 'Allergens',
  'offline.field.mayContain': 'May contain',
  'offline.yes': 'Yes',
  'offline.no': 'No',
  'offline.unknownStation': 'A station since removed',
  'offline.label.note': 'the handover note',
  'offline.label.addLine': 'the prep line “{name}”',
  'offline.label.editLine': 'a change to a prep line',
  'offline.label.removeLine': 'removing a prep line',
  'offline.label.recipe': 'the recipe “{name}”',
  'offline.label.menu': 'the menu “{name}”',
  'offline.label.ingredient': 'the ingredient “{name}”',
  'error.offline':
    'Mise can’t be reached right now, and this needs the connection. Try again once it’s back.',
  'prepPrint.print': 'Print',
  'prepPrint.title': 'Prep list',
  'prepPrint.empty': 'Nothing on the list.',
  'prepPrint.line': '{name}, {amount}',
  'recipeCard.makes': 'Makes',
  'recipeCard.prepTime': 'Prep time',
  'recipeCard.keeps': 'Keeps',
  'recipeCard.days_one': '{count} day',
  'recipeCard.days_other': '{count} days',
  'recipeCard.hours_one': '{count} hour',
  'recipeCard.hours_other': '{count} hours',
  'recipeCard.mayContain': 'May contain: {allergens}',
  'recipeCard.onThePlate': 'On the plate',
  'recipeCard.plating': 'Plating',
  'recipeCard.ownRecipe': 'its own recipe',
  'updated.title': 'Mise was updated',
  'updated.body':
    'Mise was updated. Reload to use the new version; nothing waiting to be sent is lost.',
  'updated.bodyVersion':
    'Mise was updated to {version}. Reload to use it; nothing waiting to be sent is lost.',
  'updated.reload': 'Reload',
  'error.pageTitle': 'This page didn’t open',
  'error.pageBody': 'Something went wrong while opening it. Reloading usually fixes it.',
  'access.waitingOne': '“{name}” is asking for access from outside the kitchen.',
  'access.waitingCount_one': '1 device is asking for access from outside the kitchen.',
  'access.waitingCount_other': '{count} devices are asking for access from outside the kitchen.',
  'access.checkCode': 'Check the code and approve',
  'error.incompatibleUnits':
    '{child} makes {makes}, so it can’t be measured in {unit} here. Use the same kind of unit, or change what {child} makes.',
  'error.incompatibleUnitsUsedIn':
    '{usedIn} uses {child} in {unit}, which can’t be worked out from {makes}. Change that line first, or keep the yield in the same kind of unit.',
  'recipe.shownAsWritten':
    'This recipe can’t be made to the amount asked for, so it shows as written.',
  'passwordOnly.signIn': 'Sign in with password',
  'passwordOnly.staff':
    'You’re signed in with your PIN. To add staff or change roles, passwords and PINs, sign in with your password.',
  'passwordOnly.devices':
    'You’re signed in with your PIN. To enrol tablets or let devices in from outside, sign in with your password.',
  'passwordOnly.connection':
    'You’re signed in with your PIN. To change how Mise is reached, sign in with your password.',
  'passwordOnly.backups':
    'You’re signed in with your PIN. To manage backups or see the recovery key, sign in with your password.',
  'error.passwordRequired': 'Sign in with your password to do this. A PIN can do everything else.',
} as const;

export type MessageKey = keyof typeof en;
